import { api } from "../api.js";
import { env } from "../env.js";

/**
 * Browser Web Push enable/disable. Registers the service worker, asks the
 * OS for notification permission, subscribes with the VAPID public key,
 * and registers the subscription server-side. Everything degrades to a
 * clear `false` when push isn't supported or VAPID isn't configured.
 */
export function isPushSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    typeof window !== "undefined" &&
    "PushManager" in window
  );
}

export async function enableBrowserPush(): Promise<boolean> {
  if (!isPushSupported() || !env.vapidPublicKey) return false;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;

  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;

  const existing = await reg.pushManager.getSubscription();
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      // Cast: the DOM lib types applicationServerKey as BufferSource; the
      // Uint8Array's generic buffer param trips strict assignability.
      applicationServerKey: urlBase64ToUint8Array(
        env.vapidPublicKey,
      ) as BufferSource,
    }));

  const json = sub.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return false;
  await api("/api/notifications/subscribe", {
    method: "POST",
    body: {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
    },
  });
  return true;
}

export async function disableBrowserPush(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await api("/api/notifications/subscribe", {
    method: "DELETE",
    body: { endpoint: sub.endpoint },
  }).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}

/** VAPID public keys are base64url; the Push API wants a Uint8Array. */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}
